import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, Target, Award, Shield, CheckCircle, ArrowRight,
  Building2, Globe, Clock
} from 'lucide-react';

const AboutPage = () => {
  const values = [
    {
      icon: Shield,
      title: 'Security First',
      description: 'Every solution we implement is built with security as a foundational principle, not an afterthought.'
    },
    {
      icon: Users,
      title: 'Client Partnership',
      description: "We believe in true partnerships. Your success is our success, and we're invested in your long-term growth."
    },
    {
      icon: Target,
      title: 'Proactive Approach',
      description: 'We prevent problems before they impact your business through continuous monitoring and maintenance.'
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'We maintain the highest standards in service delivery, certifications, and technical expertise.'
    },
  ];

  // All Professional Certifications (IT, Cybersecurity & Physical Security)
  const allCertifications = [
    {
      name: 'CISSP',
      badge: 'https://images.credly.com/images/6eeb0a98-33cb-4f72-bfc3-f89d65a3286c/image.png',
      height: 'h-20'
    },
    {
      name: 'CISM',
      badge: 'https://images.credly.com/images/d0891dee-6360-496c-9981-40652523b502/dbdea6794f1a6bbcc18d90eea923421aac7df6b5.png',
      height: 'h-20'
    },
    {
      name: 'Physical Security Professional (PSP)',
      badge: 'https://images.credly.com/images/2fbf8f2f-baea-46fe-b0cf-0b239f9a323b/Certification-Logos-Resize-600x600_PSP-tagline.png',
      height: 'h-20'
    },
    {
      name: 'CompTIA SecurityX',
      badge: 'https://images.credly.com/images/5343b652-c9a0-418e-bfaf-7ed5a2ddd0c4/blob',
      height: 'h-20'
    },
    {
      name: 'CompTIA Security+',
      badge: 'https://images.credly.com/images/80d8a06a-c384-42bf-ad36-db81bce5adce/blob',
      height: 'h-20'
    },
    {
      name: 'CompTIA CSIS',
      badge: 'https://images.credly.com/images/8090280a-311f-425f-a1cd-a32770b5a444/CompTIA_CSIS.png',
      height: 'h-20'
    },
    {
      name: 'ITIL 4 Foundation',
      badge: 'https://images.credly.com/images/8b943c4b-c186-4e9f-84aa-004322b76eed/image.png',
      height: 'h-20'
    },
    {
      name: 'CompTIA Network+',
      badge: 'https://images.credly.com/images/9f3a0aa9-ad62-4687-9b11-ddb626259fe2/CompTIA_Network_2B.png',
      height: 'h-20'
    },
    {
      name: 'ASIS Member',
      badge: 'https://images.credly.com/images/431ca795-43a8-4b43-a66f-323023a4db19/Membership-Credly-Digital-Badges-ASIS-Member.png',
      height: 'h-20'
    },
    {
      name: 'CompTIA A+',
      badge: 'https://images.credly.com/images/d30f7923-7d1a-43fb-9143-9fbc195a809d/CompTIA_A_2B.png',
      height: 'h-20'
    },
    {
      name: 'CompTIA CNIP',
      badge: 'https://images.credly.com/images/f308a5b0-18e3-4e93-ae15-9f27dd0a94cc/CompTIA_CNIP.png',
      height: 'h-20'
    },
    {
      name: 'CompTIA Server+',
      badge: 'https://images.credly.com/images/8293972f-573a-4d0b-be14-0bcb65e80b16/blob',
      height: 'h-20'
    },
    {
      name: 'CompTIA CIOS',
      badge: 'https://images.credly.com/images/7f7657b9-4d1b-4b8d-b5ee-5fdf6d7ccd71/04294_CompTIA_Cert_Badges_Specialist_-_CIOS.png',
      height: 'h-20'
    },
    {
      name: 'CompTIA Project+',
      badge: 'https://images.credly.com/images/dbc9a5b0-1650-4b8d-a6a6-6dba09d09509/blob',
      height: 'h-20'
    }
  ];

  // State Licenses & Business Certifications
  const licensesAndBusiness = [
    {
      title: 'NY Alarm License',
      detail: '#12000319201',
      icon: Shield
    },
    {
      title: 'NJ Alarm License',
      detail: '#34BA00198300',
      icon: Shield
    },
    {
      title: 'NJ Telecommunications',
      detail: '#34TC00300200V',
      icon: Shield
    },
    {
      title: 'Small Business Enterprise (SBE)',
      detail: 'NY, NJ Certified',
      icon: Award
    },
    {
      title: 'DoD CUI Certified',
      detail: 'Department of Defense',
      icon: Shield
    },
    {
      title: 'FEMA Surveillance',
      detail: 'Certified',
      icon: Shield
    },
    {
      title: 'FBI InfraGard',
      detail: 'Member',
      icon: Shield
    },
    {
      title: 'Police Background Verified',
      detail: 'Certified',
      icon: Shield
    }
  ];

  const partners = [
    { name: 'Microsoft', logo: 'https://www.google.com/s2/favicons?domain=microsoft.com&sz=128', height: 'h-6' },
    { name: 'AWS', logo: 'https://www.google.com/s2/favicons?domain=aws.amazon.com&sz=128', height: 'h-10' },
    { name: 'Google Cloud', logo: 'https://www.google.com/s2/favicons?domain=cloud.google.com&sz=128', height: 'h-8' },
    { name: 'VMware', logo: 'https://www.google.com/s2/favicons?domain=vmware.com&sz=128', height: 'h-6' },
    { name: 'Cisco', logo: 'https://www.google.com/s2/favicons?domain=cisco.com&sz=128', height: 'h-7' },
    { name: 'Fortinet', logo: 'https://www.google.com/s2/favicons?domain=fortinet.com&sz=128', height: 'h-5' },
    { name: 'SonicWall', logo: 'https://www.google.com/s2/favicons?domain=sonicwall.com&sz=128', height: 'h-6' },
    { name: 'Aruba', logo: 'https://customer-assets.emergentagent.com/job_mimoit-refresh/artifacts/htgunn2g_image.png', height: 'h-8' },
    { name: 'Veeam', logo: 'https://customer-assets.emergentagent.com/job_mimoit-refresh/artifacts/8tctod4p_image.png', height: 'h-6' },
    { name: 'Acronis', logo: 'https://www.google.com/s2/favicons?domain=acronis.com&sz=128', height: 'h-6' },
    { name: 'Dell', logo: 'https://www.google.com/s2/favicons?domain=dell.com&sz=128', height: 'h-8' },
    { name: 'HP', logo: 'https://www.google.com/s2/favicons?domain=hp.com&sz=128', height: 'h-10' },
    { name: 'HPE', logo: 'https://www.google.com/s2/favicons?domain=hpe.com&sz=128', height: 'h-8' },
    { name: 'Lenovo', logo: 'https://www.google.com/s2/favicons?domain=lenovo.com&sz=128', height: 'h-5' },
    { name: 'Hanwha', logo: 'https://hanwhavisionamerica.com/wp-content/uploads/2025/05/Hanwha-Vision-Logo.svg', height: 'h-7' },
    { name: 'Motorola', logo: 'https://customer-assets.emergentagent.com/job_mimoit-refresh/artifacts/p2r1unkc_image.png', height: 'h-8' },
    { name: 'Zoom', logo: 'https://www.google.com/s2/favicons?domain=zoom.us&sz=128', height: 'h-6' },
    { name: 'Adobe', logo: 'https://www.google.com/s2/favicons?domain=adobe.com&sz=128', height: 'h-8' },
    { name: 'Synology', logo: 'https://customer-assets.emergentagent.com/job_mimoit-refresh/artifacts/9f72llea_image.png', height: 'h-6' },
    { name: 'Ajax', logo: 'https://customer-assets.emergentagent.com/job_mimoit-refresh/artifacts/yxldt4cd_image.png', height: 'h-8' },
  ];

  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-mimo-primary to-slate-900 text-white py-20 md:py-28">
        <div className="container-custom">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-extrabold font-heading mb-6">
              About mimoIT
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              We're a boutique technology solutions provider specializing in tailored IT, cybersecurity, and physical electronic security services. We deliver white-glove, relationship-driven support with the same enterprise-grade solutions found in large-scale environments.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold font-heading text-slate-900 mb-6">
                Welcome to mimoIT
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p className="text-lg">
                  We deliver enterprise-grade IT, cybersecurity, and physical security solutions with personal attention and responsive service that larger providers can't match.
                </p>
                <p>
                  From cloud infrastructure and network design to endpoint protection and surveillance systems, we provide the same sophisticated technology found in Fortune 500 environments—tailored to fit your business.
                </p>
                <p>
                  <span className="font-semibold text-mimo-primary">Our team holds deep expertise across industries,</span> with a proven track record in managing complex, multi-site environments, securing public infrastructure, and standardizing technology systems for operational efficiency and peace of mind.
                </p>
              </div>
              
              <div className="mt-8 p-6 bg-mimo-primary/5 rounded-xl border-l-4 border-mimo-primary">
                <p className="text-lg font-medium text-slate-800">
                  <span className="text-mimo-primary">MIMO</span> = Multiple-Input Multiple-Output
                </p>
                <p className="text-slate-600 mt-2">
                  Like the technology we're named after, we amplify your business capabilities through multiple integrated solutions working together seamlessly.
                </p>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&q=80&w=800" 
                alt="mimoIT team collaboration" 
                className="rounded-2xl shadow-xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-mimo-primary text-white rounded-xl p-6 shadow-lg">
                <p className="text-3xl font-bold font-heading">20+</p>
                <p className="text-sm text-white/80">Years of Excellence</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <div className="w-14 h-14 rounded-xl bg-mimo-primary/10 flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-mimo-primary" />
              </div>
              <h3 className="text-2xl font-bold font-heading text-slate-900 mb-4">Our Mission</h3>
              <p className="text-slate-600 leading-relaxed">
                mimoIT's mission is to design, secure, and manage fully integrated technology environments that empower businesses to operate safely, efficiently, and without disruption.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <div className="w-14 h-14 rounded-xl bg-mimo-secondary/10 flex items-center justify-center mb-6">
                <Globe className="w-7 h-7 text-mimo-secondary" />
              </div>
              <h3 className="text-2xl font-bold font-heading text-slate-900 mb-4">Our Vision</h3>
              <p className="text-slate-600 leading-relaxed">
                To redefine business technology by unifying IT, cybersecurity, communications, and physical security into one seamless, secure ecosystem.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold font-heading text-slate-900 mb-4">
              Our Core Values
            </h2>
            <p className="text-lg text-slate-600">
              The principles that guide everything we do
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={value.title} className="text-center" data-testid={`value-${index}`}>
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-mimo-primary to-mimo-accent flex items-center justify-center mx-auto mb-6">
                  <value.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold font-heading text-slate-900 mb-3">{value.title}</h3>
                <p className="text-slate-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Strategic Partnerships */}
      <section className="section-padding bg-slate-50 overflow-hidden">
        <div className="container-custom mb-8">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold font-heading text-slate-900 mb-4">
              Strategic Technology Partnerships
            </h2>
            <p className="text-lg text-slate-600">
              We collaborate with leading technology vendors to deliver best-in-class solutions
            </p>
          </div>
        </div>
        
        {/* Scrolling Banner */}
        <div className="relative py-8 bg-white">
          <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-white to-transparent z-10" />
          <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-white to-transparent z-10" />
          
          <div className="flex overflow-hidden">
            <div className="flex animate-scroll items-center gap-16 px-8">
              {/* First set */}
              {partners.map((partner, index) => (
                <img 
                  key={`first-${index}`}
                  src={partner.logo} 
                  alt={partner.name} 
                  className={`${partner.height} w-auto object-contain grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-all duration-300 flex-shrink-0`}
                />
              ))}
              {/* Duplicate set for seamless loop */}
              {partners.map((partner, index) => (
                <img 
                  key={`second-${index}`}
                  src={partner.logo} 
                  alt={partner.name} 
                  className={`${partner.height} w-auto object-contain grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-all duration-300 flex-shrink-0`}
                />
              ))}
            </div>
          </div>
        </div>
        
        <div className="container-custom mt-8">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-6 text-center shadow-sm">
              <h3 className="font-bold font-heading text-slate-900 mb-2">Certified Expertise</h3>
              <p className="text-slate-600 text-sm">Current certifications across all partner platforms</p>
            </div>
            <div className="bg-white rounded-xl p-6 text-center shadow-sm">
              <h3 className="font-bold font-heading text-slate-900 mb-2">Priority Support</h3>
              <p className="text-slate-600 text-sm">Direct access to vendor support channels</p>
            </div>
            <div className="bg-white rounded-xl p-6 text-center shadow-sm">
              <h3 className="font-bold font-heading text-slate-900 mb-2">Best Pricing</h3>
              <p className="text-slate-600 text-sm">Competitive pricing through partnerships</p>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="section-padding bg-slate-50 overflow-hidden">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold font-heading text-slate-900 mb-4">
              Certifications & Credentials
            </h2>
            <p className="text-slate-600 text-lg">
              Licensed, certified, and committed to the highest standards of excellence
            </p>
          </div>
          
          {/* Professional Certifications - Scrolling Banner */}
          <div className="mb-16">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold font-heading text-slate-900 mb-2">
                Professional Certifications
              </h3>
              <p className="text-slate-600">Industry-recognized expertise in IT, cybersecurity, and physical security</p>
            </div>
            
            {/* Scrolling Banner */}
            <div className="relative py-8 bg-white rounded-2xl">
              <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-white to-transparent z-10" />
              <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-white to-transparent z-10" />
              
              <div className="flex overflow-hidden">
                <div className="flex animate-scroll items-center gap-12 px-8">
                  {/* First set */}
                  {allCertifications.map((cert, index) => (
                    <div 
                      key={`first-${index}`}
                      className="flex-shrink-0 flex items-center justify-center p-4"
                      data-testid={`cert-${index}`}
                    >
                      <img 
                        src={cert.badge} 
                        alt={cert.name}
                        className={`${cert.height} w-auto object-contain hover:scale-110 transition-transform`}
                        title={cert.name}
                      />
                    </div>
                  ))}
                  {/* Duplicate set for seamless loop */}
                  {allCertifications.map((cert, index) => (
                    <div 
                      key={`second-${index}`}
                      className="flex-shrink-0 flex items-center justify-center p-4"
                    >
                      <img 
                        src={cert.badge} 
                        alt={cert.name}
                        className={`${cert.height} w-auto object-contain hover:scale-110 transition-transform`}
                        title={cert.name}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* State and Federal Certifications */}
          <div>
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold font-heading text-slate-900 mb-2">
                State and Federal Certifications, Licenses, and Clearances
              </h3>
              <p className="text-slate-600">Fully licensed and certified to serve your business needs</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              {licensesAndBusiness.map((item, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-xl p-6 shadow-sm border border-slate-200"
                  data-testid={`license-${index}`}
                >
                  <div className="w-12 h-12 rounded-lg bg-mimo-primary/10 flex items-center justify-center mb-4">
                    <item.icon className="w-6 h-6 text-mimo-primary" />
                  </div>
                  <h4 className="font-bold text-slate-900 mb-2">{item.title}</h4>
                  <p className="text-sm text-slate-600">{item.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold font-heading text-slate-900 mb-6">
                Where We Serve
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                While our headquarters are in the Tri-State Area, we provide comprehensive IT services to clients across the nation. Our remote monitoring and support capabilities, combined with strategic on-site support, allow us to serve businesses wherever they operate.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-mimo-primary/10 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-5 h-5 text-mimo-primary" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900">New Jersey</h4>
                    <p className="text-slate-600">Full-service coverage with on-site support</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-mimo-primary/10 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-5 h-5 text-mimo-primary" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900">New York</h4>
                    <p className="text-slate-600">Manhattan office with metro-wide coverage</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-mimo-primary/10 flex items-center justify-center flex-shrink-0">
                    <Globe className="w-5 h-5 text-mimo-primary" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900">Nationwide & International</h4>
                    <p className="text-slate-600">Remote management with partner network for on-site needs</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Supplier Diversity */}
      <section className="section-padding bg-gradient-to-br from-slate-50 to-white">
        <div className="container-custom">
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-mimo-primary/10 rounded-full mb-4">
                <Award className="w-5 h-5 text-mimo-primary" />
                <span className="text-sm font-semibold text-mimo-primary uppercase tracking-wider">
                  Supplier Diversity Partner
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold font-heading text-slate-900 mb-6">
                Certified Minority Business Enterprise
              </h2>
              <p className="text-xl text-slate-600 leading-relaxed max-w-3xl mx-auto">
                Partner with a certified MBE to meet your supplier diversity goals while gaining access to enterprise-grade IT, cybersecurity, and physical security solutions.
              </p>
            </div>

            {/* Value Proposition Cards */}
            <div className="grid md:grid-cols-3 gap-6 mb-16">
              <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-mimo-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <CheckCircle className="w-6 h-6 text-mimo-primary" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Enterprise Capabilities</h3>
                <p className="text-slate-600">
                  Fortune 500-level solutions delivered by a certified diverse supplier—the best of both worlds.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-mimo-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-mimo-primary" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Compliance Ready</h3>
                <p className="text-slate-600">
                  Help meet federal, state, and corporate supplier diversity mandates with a fully certified partner.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-mimo-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Target className="w-6 h-6 text-mimo-primary" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Proven Track Record</h3>
                <p className="text-slate-600">
                  Trusted by public entities and private organizations across the Tri-State Area and nationwide.
                </p>
              </div>
            </div>

            {/* Certifications Grid */}
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <div className="text-center mb-10">
                <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
                  Diversity Supplier Certifications
                </h3>
                <p className="text-slate-600">
                  Certified to deliver services and solutions as diverse suppliers to public and private institutions
                </p>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 mb-10">
                {/* NY MBE */}
                <div className="flex flex-col items-center gap-4 group">
                  <div className="relative h-24 flex items-center justify-center">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_mimoit-refresh/artifacts/dirls91z_image.png" 
                      alt="New York State Minority Business Enterprise"
                      className="h-20 w-auto object-contain grayscale group-hover:grayscale-0 opacity-70 group-hover:opacity-100 transition-all duration-300"
                    />
                  </div>
                  <div className="text-center">
                    <p className="font-semibold text-slate-900 text-sm mb-1">NY State MBE</p>
                    <p className="text-xs text-slate-500">Minority Business Enterprise</p>
                  </div>
                </div>

                {/* NJ State MBE */}
                <div className="flex flex-col items-center gap-4 group">
                  <div className="relative h-24 flex items-center justify-center">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_mimoit-refresh/artifacts/9h4fwmm8_image.png" 
                      alt="New Jersey State Minority Business Enterprise"
                      className="h-20 w-auto object-contain grayscale group-hover:grayscale-0 opacity-70 group-hover:opacity-100 transition-all duration-300"
                    />
                  </div>
                  <div className="text-center">
                    <p className="font-semibold text-slate-900 text-sm mb-1">NJ State MBE</p>
                    <p className="text-xs text-slate-500">Minority Business Enterprise</p>
                  </div>
                </div>

                {/* MBE */}
                <div className="flex flex-col items-center gap-4 group">
                  <div className="relative h-24 flex items-center justify-center">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_mimoit-refresh/artifacts/4tx56yv0_image.png" 
                      alt="Minority Business Enterprise"
                      className="h-20 w-auto object-contain grayscale group-hover:grayscale-0 opacity-70 group-hover:opacity-100 transition-all duration-300"
                    />
                  </div>
                  <div className="text-center">
                    <p className="font-semibold text-slate-900 text-sm mb-1">MBE</p>
                    <p className="text-xs text-slate-500">Certified Enterprise</p>
                  </div>
                </div>

                {/* Port Authority */}
                <div className="flex flex-col items-center gap-4 group">
                  <div className="relative h-24 flex items-center justify-center">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_mimoit-refresh/artifacts/3vdubvon_image.png" 
                      alt="Port Authority of NY & NJ"
                      className="h-24 w-auto object-contain grayscale group-hover:grayscale-0 opacity-70 group-hover:opacity-100 transition-all duration-300"
                    />
                  </div>
                  <div className="text-center">
                    <p className="font-semibold text-slate-900 text-sm mb-1">Port Authority</p>
                    <p className="text-xs text-slate-500">NY & NJ Certified</p>
                  </div>
                </div>
              </div>

              {/* MBE Badge - Enhanced */}
              <div className="pt-8 border-t border-slate-200 flex justify-center">
                <div className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-mimo-primary/10 to-mimo-accent/10 rounded-2xl border-2 border-mimo-primary/30">
                  <Award className="w-7 h-7 text-mimo-primary" />
                  <div className="text-left">
                    <p className="font-bold text-slate-900 text-lg">Minority Business Enterprise</p>
                    <p className="text-sm text-slate-600">Certified & Verified</p>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-12 text-center bg-slate-900 rounded-2xl p-8 md:p-12">
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                Ready to Partner with a Certified MBE?
              </h3>
              <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
                Let's discuss how mimoIT can help you meet your supplier diversity goals while delivering world-class technology solutions.
              </p>
              <Link 
                to="/contact" 
                className="inline-flex items-center gap-2 bg-mimo-primary hover:bg-mimo-primary/90 text-white px-8 py-4 rounded-full font-semibold transition-all"
              >
                Contact Our Team
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold font-heading text-slate-900 mb-4">
            Ready to Partner with mimoIT?
          </h2>
          <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
            Discover how our team can help transform your IT infrastructure and protect your business.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-primary flex items-center justify-center gap-2">
              Schedule a Consultation
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/services" className="btn-outline">
              Explore Our Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
